// src/components/views/ProjectView.tsx
import React, { useState, useEffect, useMemo } from 'react'
import { useDeleteProjectMutation, useGetProjectQuery } from '../../../services/projectsApi'
import { useAppSelector, useAppDispatch, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { useNavigate } from 'react-router-dom'
import { useGetContentMetadataQuery } from '../../../services/contentApi'
import { selectDatasetSources } from '../../../store/slices/datasetSlice'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { Model as ImportedModel } from '../../../types/models/model'
import UploadWizard from '../../wizards/UploadWizard'
import {
  DashboardContainer,
  ScrollableContent,
  DashboardContent,
  StatsRow,
  StatsCol,
} from './styles'
import {
  ProjectInformationPanel,
  DatasetAnalyticsPanel,
  ClassDistributionSection,
  ModelStatisticsSection,
  ModelPerformanceSection,
  QuickActionsSection,
  AnnotationProgressSection,
} from './components'
import QuickStatsSection from './components/QuickStatsSection'

interface ModelMetrics {
  'validation:mAP50-95': number
  'validation:precision': number
  'validation:recall': number
  [key: string]: number
}

interface Model extends Omit<ImportedModel, 'metrics'> {
  metrics?: ModelMetrics
}

const ProjectOverview: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const datasetSources = useAppSelector(selectDatasetSources)

  const {
    data: projectData,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
  } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const {
    data: annoStats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
  } = useAnnotationStats(selectedProjectId || '', projectData?.datasetid || '', datasetSources)

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/')
    if (
      selectedProjectId &&
      pathSegments.length === 3 &&
      pathSegments[1] === 'overview' &&
      pathSegments[2] === selectedProjectId
    ) {
      navigate(`/overview/${selectedProjectId}/overview`, { replace: true })
    }
  }, [navigate, selectedProjectId])

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  )

  const [deleteProject] = useDeleteProjectMutation()
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showUploadDataModal, setShowUploadDataModal] = useState(false)
  const [showUploadWizard, setShowUploadWizard] = useState(false)
  const [selectedDataset, setSelectedDataset] = useState<string>('all')
  const [uploading, setUploading] = useState(false)
  const [hasUserDismissedUpload, setHasUserDismissedUpload] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  useEffect(() => {
    if (annoStats?.statistics && !hasUserDismissedUpload) {
      const totalImages =
        (annoStats.statistics.total_annotated_images || 0) +
        (annoStats.statistics.total_unannotated_images || 0)
      if (totalImages === 0 && !uploading) {
        setShowUploadDataModal(true)
      }
    }
  }, [annoStats?.statistics, projectData, uploading, hasUserDismissedUpload])

  // No automatic model selection - users should select models on pages that need them

  const handleUploadData = () => {
    setUploading(true)
    setShowUploadDataModal(false)
    setShowUploadWizard(true)
  }

  const handleDismissUpload = () => {
    setShowUploadDataModal(false)
    setHasUserDismissedUpload(true)
  }

  const navigateToDatasets = () => {
    setShowUploadWizard(false)
  }

  // Derived stats and gating flags (computed after quickStats is defined below)
  let annotated = 0 as number
  let unannotated = 0 as number
  let totalAnnotations = 0 as number
  let classesCount = 0 as number
  let totalImages = 0 as number
  let canCreateModel = false
  let canLabelData = false

  const handleDeleteProject = async () => {
    try {
      await deleteProject(selectedProjectId || '').unwrap()
      navigate('/projects')
    } catch (err) {
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to delete project'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const countTrainedAndDeployedModels = (
    models: ImportedModel[]
  ): { trained: number; deployed: number } => {
    return models?.reduce(
      (acc, model) => {
        if (model.deployment?.status.toLowerCase() === 'in_service') {
          acc.deployed++
          acc.trained++
        } else if (model.state.toLowerCase() === 'trained') {
          acc.trained++
        }
        return acc
      },
      { trained: 0, deployed: 0 }
    )
  }

  const modelCounts = modelsData?.models
    ? countTrainedAndDeployedModels(modelsData.models)
    : { trained: 0, deployed: 0 }

  const quickStats = useMemo(
    () => ({
      datasources: (metadata?.metadata?.datasource || []).length,
      classes: (() => {
        const apc = annoStats?.statistics?.annotations_per_class
        if (!apc) return 0
        return Object.values(apc)
          .map((v: any) => ({ name: v?.name, count: v?.count }))
          .filter((v) => v && v.name !== 'Unknown' && v.name !== 'null' && (v.count || 0) > 0)
          .length
      })(),
      images:
        (annoStats?.statistics?.total_annotated_images || 0) +
        (annoStats?.statistics?.total_unannotated_images || 0),
      annotations: annoStats?.statistics?.total_annotations || 0,
    }),
    [metadata?.metadata?.datasource, annoStats?.statistics]
  )

  // Now that quickStats is initialized, compute gating values safely
  annotated = (annoStats?.statistics?.total_annotated_images || 0) as number
  unannotated = (annoStats?.statistics?.total_unannotated_images || 0) as number
  totalAnnotations = (annoStats?.statistics?.total_annotations || 0) as number
  classesCount = quickStats.classes
  totalImages = annotated + unannotated
  canCreateModel = totalImages > 0 && totalAnnotations >= 10 && classesCount >= 1
  // Allow labeling whenever there are images, even if currently 100% labeled
  canLabelData = totalImages > 0

  const hasDatasets = (metadata?.metadata?.datasource || []).length > 0
  const hasImages = totalImages > 0
  const hasClasses = classesCount > 0
  const hasModels = (modelsData?.models?.length || 0) > 0
  const hasTrainedModels = !!modelsData?.models?.some(
    (m: ImportedModel) => m.state?.toLowerCase() === 'trained'
  )

  const datasetChartData = {
    labels: metadata?.metadata?.datasource || [],
    datasets: [
      {
        label: 'Images',
        data:
          metadata?.metadata?.datasource?.map((source: string) => {
            return annoStats?.statistics?.datasource_distributions?.[source]?.images || 0
          }) || [],
        backgroundColor: 'rgba(13, 202, 240, 0.7)',
        borderColor: 'rgba(13, 202, 240, 1)',
        borderWidth: 1,
      },
      {
        label: 'Annotations',
        data:
          metadata?.metadata?.datasource?.map((source: string) => {
            return annoStats?.statistics?.datasource_distributions?.[source]?.annotations || 0
          }) || [],
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
        borderColor: 'rgba(25, 135, 84, 1)',
        borderWidth: 1,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#ffffff',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
        },
      },
    },
  }

  const modelPerformanceData = {
    labels:
      modelsData?.models
        ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
        ?.map((model: ImportedModel) => model.name) || [],
    datasets: [
      {
        label: 'mAP50-95',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:mAP50-95'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(13, 202, 240, 0.7)',
        borderColor: 'rgba(13, 202, 240, 1)',
        borderWidth: 1,
      },
      {
        label: 'Precision',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:precision'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
        borderColor: 'rgba(25, 135, 84, 1)',
        borderWidth: 1,
      },
      {
        label: 'Recall',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:recall'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(220, 53, 69, 0.7)',
        borderColor: 'rgba(220, 53, 69, 1)',
        borderWidth: 1,
      },
    ],
  }

  const modelPerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
          callback: (value: number) => `${value}%`,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#ffffff',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`,
        },
      },
    },
  }

  const isLoading = useMemo(() => {
    // Only consider loading states that affect the data we need
    const dataLoading = isLoadingStats || isLoadingProject
    const dataFetching = isFetchingStats || isFetchingProject
    const dataMissing = !annoStats?.statistics || !metadata?.metadata

    return dataLoading || (dataFetching && dataMissing)
  }, [
    isLoadingStats,
    isLoadingProject,
    isFetchingStats,
    isFetchingProject,
    annoStats?.statistics,
    metadata?.metadata,
  ])

  return (
    <DashboardContainer>
      <ScrollableContent>
        <DashboardContent>
          <QuickStatsSection loading={isLoading} stats={quickStats} />

          <StatsRow>
            <StatsCol xs={12} lg={8}>
              <QuickActionsSection
                onUploadData={() => navigate(`/datasets/${selectedProjectId}/content`)}
                onCreateModel={() => canCreateModel && navigate(`/training/${selectedProjectId}`)}
                onGoToLabeling={() => canLabelData && navigate(`/labeling/${selectedProjectId}`)}
                disableCreateModel={!canCreateModel}
                disableLabeling={!canLabelData}
                createModelHint={
                  canCreateModel ? undefined : 'Need data with at least 10 annotations and 1 class'
                }
                labelingHint={canLabelData ? undefined : 'Upload data first to start labeling'}
                onGoToDeployment={() =>
                  hasTrainedModels && navigate(`/deployment/${selectedProjectId}`)
                }
                disableDeployment={!hasTrainedModels}
                deploymentHint={hasTrainedModels ? undefined : 'Train a model before deploying'}
              />
            </StatsCol>
            <StatsCol xs={12} lg={4}>
              <AnnotationProgressSection annotated={annotated} unannotated={unannotated} />
            </StatsCol>
          </StatsRow>

          <StatsRow>
            <StatsCol xs={12} lg={5}>
              <ProjectInformationPanel
                projectId={projectData?.id || ''}
                description={projectData?.description || ''}
                annotationType={projectData?.annotation_type || ''}
                totalItems={projectData?.count || 0}
                updatedAt={projectData?.updated_at || ''}
              />
            </StatsCol>
            {hasDatasets && hasImages && (
              <StatsCol xs={12} lg={7}>
                <DatasetAnalyticsPanel
                  loading={isLoading}
                  chartData={datasetChartData}
                  chartOptions={chartOptions}
                />
              </StatsCol>
            )}
          </StatsRow>

          {hasClasses && hasImages && (
            <ClassDistributionSection
              loading={isLoading}
              selectedDataset={selectedDataset}
              onDatasetChange={setSelectedDataset}
              dataSources={metadata?.metadata?.datasource || []}
            />
          )}

          {hasModels && (
            <StatsRow>
              <StatsCol xs={12} md={3}>
                <ModelStatisticsSection
                  totalModels={modelsData!.models.length}
                  trainedModels={modelCounts.trained}
                  deployedModels={modelCounts.deployed}
                />
              </StatsCol>
              <StatsCol xs={12} md={9}>
                <ModelPerformanceSection
                  loading={!modelsData?.models}
                  chartData={modelPerformanceData}
                  chartOptions={modelPerformanceOptions}
                />
              </StatsCol>
            </StatsRow>
          )}
        </DashboardContent>
      </ScrollableContent>

      <ConfirmDialog
        show={showConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
        onConfirmRight={handleDeleteProject}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        show={showUploadDataModal}
        onHide={handleDismissUpload}
        onConfirmRight={handleUploadData}
        title="Upload Data"
        message="You need to upload data to do anything useful. Click here to go to the datasets page."
        confirmRightText="Upload Data"
        cancelText="Cancel"
        variant="primary"
      />

      <UploadWizard show={showUploadWizard} onHide={navigateToDatasets} />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </DashboardContainer>
  )
}

export default ProjectOverview
